import fijiWaterUrl from './fiji-water.png';
import { loadGeminiResponse } from './gemini.js';

const CHAOS_PHRASES = [
  'FIJI MODE ACTIVATED',
  'HYDRATION OVERLOAD',
  'ARTESIAN CHAOS',
  'BOTTLED INSANITY',
  'STAY THIRSTY MY FRIEND',
  'RAINBOW FIJI ENERGY',
];

let chaosActive = false;
let chaosOverlay = null;
let chaosInterval = null;
let chaosAudioCtx = null;
let colorPaletteIndex = 0;

const CHAOS_PALETTES = [
  { hue: 0,   label: 'CLASSIC CHAOS' },
  { hue: 60,  label: 'SOLAR FLARE' },
  { hue: 120, label: 'TOXIC FIJI' },
  { hue: 180, label: 'CYBER HYDRATION' },
  { hue: 240, label: 'DEEP ARTESIAN' },
  { hue: 300, label: 'NEON ISLAND' },
];

function playChaosBlip() {
  try {
    if (!chaosAudioCtx) {
      chaosAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    const osc = chaosAudioCtx.createOscillator();
    const gain = chaosAudioCtx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.value = 120 + Math.random() * 880;
    gain.gain.value = 0.04;
    osc.connect(gain);
    gain.connect(chaosAudioCtx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, chaosAudioCtx.currentTime + 0.15);
    osc.stop(chaosAudioCtx.currentTime + 0.15);
  } catch {
    /* audio optional */
  }
}

function spawnBottle(container) {
  const wrap = document.createElement('div');
  wrap.className = 'fiji-bottle-wrap';

  const img = document.createElement('img');
  img.src = fijiWaterUrl;
  img.alt = 'Fiji water bottle';
  img.className = 'fiji-bottle';
  img.draggable = false;
  wrap.appendChild(img);

  const size = 50 + Math.random() * 130;
  const startX = Math.random() * (window.innerWidth - size);
  const startY = Math.random() * window.innerHeight;
  const duration = 2 + Math.random() * 3;
  const spinDir = Math.random() > 0.5 ? 1 : -1;
  const spinSpeed = 0.25 + Math.random() * 0.75;

  wrap.style.width = `${size}px`;
  wrap.style.left = `${startX}px`;
  wrap.style.top = `${startY}px`;
  wrap.style.setProperty('--duration', `${duration}s`);
  wrap.style.setProperty('--spin-dir', spinDir === -1 ? 'reverse' : 'normal');
  wrap.style.setProperty('--spin-speed', `${spinSpeed}s`);
  wrap.style.setProperty('--drift-x', `${-120 + Math.random() * 240}px`);
  wrap.style.setProperty('--hue', `${Math.random() * 360}deg`);

  container.appendChild(wrap);
  setTimeout(() => wrap.remove(), duration * 1000);
}

function spawnRainbowBurst(container) {
  const burst = document.createElement('div');
  burst.className = 'rainbow-burst';
  burst.style.left = `${Math.random() * 100}%`;
  burst.style.top = `${Math.random() * 100}%`;
  burst.style.setProperty('--scale', `${0.5 + Math.random() * 2}`);
  container.appendChild(burst);
  setTimeout(() => burst.remove(), 1200);
}

function spawnChaosText(container) {
  const text = document.createElement('div');
  text.className = 'chaos-text';
  text.textContent = CHAOS_PHRASES[Math.floor(Math.random() * CHAOS_PHRASES.length)];
  text.style.left = `${Math.random() * 80 + 10}%`;
  text.style.top = `${Math.random() * 80 + 10}%`;
  text.style.setProperty('--rot', `${-30 + Math.random() * 60}deg`);
  container.appendChild(text);
  setTimeout(() => text.remove(), 2000);
}

function shiftChaosColors() {
  if (!chaosActive || !chaosOverlay) return;

  colorPaletteIndex = (colorPaletteIndex + 1) % CHAOS_PALETTES.length;
  const palette = CHAOS_PALETTES[colorPaletteIndex];

  chaosOverlay.dataset.palette = colorPaletteIndex;
  document.body.style.setProperty('--chaos-hue', `${palette.hue}deg`);

  const msg = chaosOverlay.querySelector('.chaos-center-msg');
  if (msg) {
    msg.textContent = palette.label;
    msg.classList.remove('palette-flash');
    void msg.offsetWidth;
    msg.classList.add('palette-flash');
  }

  playChaosBlip();
  for (let i = 0; i < 5; i++) {
    setTimeout(() => spawnRainbowBurst(chaosOverlay), i * 60);
  }
}

function handleChaosKeydown(e) {
  if (!chaosActive) return;

  if (e.key === 'Escape') {
    e.preventDefault();
    stopChaos();
    return;
  }

  if (e.code === 'Space' || e.key === ' ') {
    e.preventDefault();
    e.stopImmediatePropagation();
    shiftChaosColors();
  }
}

function startChaos() {
  if (chaosActive) return;
  chaosActive = true;
  colorPaletteIndex = 0;
  document.body.classList.add('fiji-chaos-active');

  chaosOverlay = document.createElement('div');
  chaosOverlay.id = 'fiji-chaos-overlay';
  chaosOverlay.tabIndex = -1;
  chaosOverlay.innerHTML = `
    <div class="chaos-blackout"></div>
    <div class="chaos-rainbow-layer"></div>
    <div class="chaos-flash-layer"></div>
    <div class="chaos-bottles"></div>
    <div class="chaos-center-msg">🥚 YOU FOUND THE EGG 🥚</div>
  `;
  document.body.appendChild(chaosOverlay);
  chaosOverlay.dataset.palette = '0';
  document.body.style.setProperty('--chaos-hue', '0deg');

  document.getElementById('easter-egg')?.blur();
  chaosOverlay.focus();

  chaosOverlay.addEventListener('pointerdown', stopChaos);
  chaosOverlay.addEventListener('keydown', handleChaosKeydown);

  const bottlesLayer = chaosOverlay.querySelector('.chaos-bottles');

  for (let i = 0; i < 25; i++) {
    setTimeout(() => spawnBottle(bottlesLayer), i * 80);
  }

  chaosInterval = setInterval(() => {
    if (!chaosActive) return;
    spawnBottle(bottlesLayer);
    spawnRainbowBurst(chaosOverlay);
    if (Math.random() > 0.6) spawnChaosText(chaosOverlay);
    if (Math.random() > 0.7) playChaosBlip();
  }, 200);
}

function stopChaos() {
  if (!chaosActive) return;
  chaosActive = false;
  document.body.classList.remove('fiji-chaos-active');
  document.body.style.removeProperty('--chaos-hue');
  clearInterval(chaosInterval);
  chaosInterval = null;
  chaosOverlay?.remove();
  chaosOverlay = null;
}

document.addEventListener('DOMContentLoaded', () => {
  loadGeminiResponse();

  const egg = document.getElementById('easter-egg');

  egg?.addEventListener('click', () => {
    startChaos();
  });

  egg?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.code === 'Space' || e.key === ' ') {
      e.preventDefault();
      startChaos();
    }
  });

  const teeWrap = document.getElementById('merch-tee-wrap');
  teeWrap?.addEventListener('click', (e) => {
    if (!window.matchMedia('(hover: none)').matches) return;
    e.preventDefault();
    teeWrap.classList.toggle('show-back');
  });

  window.addEventListener('keydown', handleChaosKeydown, true);
  window.addEventListener('keyup', (e) => {
    if (chaosActive && (e.code === 'Space' || e.key === ' ')) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);
});
